import { useState, useRef, useEffect, useCallback } from 'react';
import { X, AlertTriangle } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { Select } from '@/components/base/select/select';
import { ModalOverlay, Modal, Dialog } from '@/components/application/modals/modal';
import { useTranslation } from 'react-i18next';
import { storage } from '@/lib/local-storage';
import type { User } from '@/data/api/users';

interface ResetUserDataModalProps {
    user: User | null;
    isOpen: boolean;
    onClose: () => void;
}

type RunStatus = 'idle' | 'running' | 'completed' | 'error';

interface LogEntry {
    line: string;
    type: 'info' | 'warning' | 'error' | 'success' | 'done';
}

const modeOptions = (t: (key: string) => string) => [
    { id: 'dry_run', label: t('team.resetData.dryRun') },
    { id: 'force', label: t('team.resetData.force') },
];

export const ResetUserDataModal = ({ user, isOpen, onClose }: ResetUserDataModalProps) => {
    const { t } = useTranslation(['pages:companies', 'common']);
    const [mode, setMode] = useState<'dry_run' | 'force'>('dry_run');
    const [status, setStatus] = useState<RunStatus>('idle');
    const [logs, setLogs] = useState<LogEntry[]>([]);
    const terminalRef = useRef<HTMLDivElement>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    // Auto-scroll terminal to bottom
    useEffect(() => {
        if (terminalRef.current) {
            terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
        }
    }, [logs]);

    // Reset state when modal opens
    useEffect(() => {
        if (isOpen) {
            setMode('dry_run');
            setStatus('idle');
            setLogs([]);
        }
    }, [isOpen]);

    // Cleanup abort controller on unmount
    useEffect(() => {
        return () => {
            abortControllerRef.current?.abort();
        };
    }, []);

    const handleRun = useCallback(async () => {
        if (!user) return;

        const token = storage.getItem("access_token");
        if (!token) return;

        setStatus('running');
        setLogs([]);

        const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:8000";
        const versionPrefix = import.meta.env.VITE_API_VERSION_PREFIX || "/api/v1";
        const cleanBaseUrl = baseUrl.replace(/\/$/, "");
        const cleanVersionPrefix = versionPrefix.startsWith("/") ? versionPrefix : `/${versionPrefix}`;
        const url = `${cleanBaseUrl}${cleanVersionPrefix}/admin/users/${user.user_id}/reset-data?mode=${mode}`;

        const controller = new AbortController();
        abortControllerRef.current = controller;

        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                signal: controller.signal,
            });

            if (!response.ok) {
                const errorText = await response.text().catch(() => response.statusText);
                setLogs([{ line: `HTTP ${response.status}: ${errorText}`, type: 'error' }]);
                setStatus('error');
                return;
            }

            const reader = response.body?.getReader();
            if (!reader) {
                setStatus('error');
                return;
            }

            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });

                // Parse SSE lines from buffer
                const lines = buffer.split('\n');
                buffer = lines.pop() || ''; // Keep incomplete line in buffer

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const entry: LogEntry = JSON.parse(line.slice(6));
                            if (entry.type === 'done') {
                                setStatus('completed');
                            } else {
                                setLogs(prev => [...prev, entry]);
                                if (entry.type === 'error') {
                                    setStatus('error');
                                }
                            }
                        } catch {
                            // Skip malformed SSE lines
                        }
                    }
                }
            }

            // If we finished reading without explicit done/error, mark completed
            setStatus(prev => prev === 'running' ? 'completed' : prev);
        } catch (err: any) {
            if (err.name !== 'AbortError') {
                setLogs(prev => [...prev, { line: `Connection error: ${err.message}`, type: 'error' }]);
                setStatus('error');
            }
        }
    }, [user, mode]);

    const handleClose = () => {
        if (status === 'running') return; // Prevent closing while running
        abortControllerRef.current?.abort();
        onClose();
    };

    const getLineColor = (type: string) => {
        switch (type) {
            case 'error': return 'text-red-400';
            case 'warning': return 'text-yellow-400';
            case 'success': return 'text-green-400';
            default: return 'text-gray-300';
        }
    };

    const userName = user?.full_name || `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.email || '';

    return (
        <ModalOverlay isOpen={isOpen} onOpenChange={(open) => !open && handleClose()}>
            <Modal>
                <Dialog>
                    <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary w-full max-w-xl">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-semibold text-primary">
                                {t('team.resetData.title')}
                            </h2>
                            <button
                                onClick={handleClose}
                                className="text-tertiary hover:text-primary disabled:opacity-50"
                                aria-label={t('common:buttons.close')}
                                disabled={status === 'running'}
                            >
                                <X className="size-5" />
                            </button>
                        </div>

                        {/* User info */}
                        <p className="text-sm text-tertiary mb-4">
                            {userName} ({user?.email})
                        </p>

                        {/* Warning banner */}
                        <div className="flex items-start gap-3 rounded-lg bg-warning-secondary p-3 mb-4">
                            <AlertTriangle className="size-5 text-warning-solid shrink-0 mt-0.5" />
                            <p className="text-sm text-warning-primary">
                                {t('team.resetData.warning')}
                            </p>
                        </div>

                        {/* Mode selector */}
                        <div className="mb-4">
                            <Select
                                label={t('team.resetData.mode')}
                                items={modeOptions(t)}
                                selectedKey={mode}
                                onSelectionChange={(key) => setMode(key as 'dry_run' | 'force')}
                                size="md"
                                isDisabled={status === 'running'}
                            >
                                {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
                            </Select>
                        </div>

                        {/* Terminal output */}
                        {logs.length > 0 && (
                            <div
                                ref={terminalRef}
                                className="bg-gray-900 rounded-lg p-3 mb-4 max-h-72 overflow-y-auto font-mono text-xs leading-relaxed"
                            >
                                {logs.map((entry, i) => (
                                    <div key={i} className={getLineColor(entry.type)}>
                                        {entry.line}
                                    </div>
                                ))}
                                {status === 'completed' && (
                                    <div className="text-green-400 mt-1 font-semibold">
                                        {t('team.resetData.completed')}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Actions */}
                        <div className="flex justify-end gap-3">
                            <Button
                                onClick={handleClose}
                                color="secondary"
                                size="md"
                                disabled={status === 'running'}
                            >
                                {t('team.resetData.close')}
                            </Button>
                            <Button
                                onClick={handleRun}
                                color={mode === 'force' ? 'primary-destructive' : 'primary'}
                                size="md"
                                isLoading={status === 'running'}
                                disabled={status === 'running'}
                            >
                                {status === 'running' ? t('team.resetData.running') : t('team.resetData.run')}
                            </Button>
                        </div>
                    </div>
                </Dialog>
            </Modal>
        </ModalOverlay>
    );
};
